import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, ElementRef, HostListener, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { QaiLoadingComponent, QaiReportingCardComponent } from "@qai/ui";
import * as Highcharts from 'highcharts';
import { TRANSLOCO_SCOPE, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { TranslationService } from 'src/app/services/translation.service';
import { IIndividualAssignmentEngagementStatsResponse } from 'src/app/interfaces/reportingV2/iindividual-assignment-engagement-stats-response';
import { LocaleService } from 'src/app/services/locale.service';
import { formatHoursMinutesRounded } from 'src/app/utils/date-parser.util';

@Component({
  selector: 'app-practice-statistics',
  templateUrl: './practice-statistics.component.html',
  styleUrls: ['./practice-statistics.component.scss'],
  standalone: true,
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'reportingV2',
    }
  ],
  imports: [CommonModule, QaiReportingCardComponent, QaiLoadingComponent, TranslocoModule],
})
export class PracticeStatisticsComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('donutChartContainer', { static: false }) donutChartContainer!: ElementRef;
  @Input() engagementStats: IIndividualAssignmentEngagementStatsResponse | null = null;
  @Input() isLoadingEngagementStats: boolean = false;
  
  donutChart: Highcharts.Chart | undefined;
  isTranslationLoaded: boolean = false;
  private resizeTimeout: any;

  constructor(
    private translocoService: TranslocoService,
    private translationService: TranslationService,
    protected readonly localeService: LocaleService,
  ) { }

  ngOnInit() {
    this.translationService.loadScopedTranslation('reportingV2').then(() => {
      this.isTranslationLoaded = true;
      // Small delay to ensure DOM is rendered
      setTimeout(() => {
        this.initDonutChart();
      }, 100);
    });
  }

  ngAfterViewInit() {
    // Chart will be initialized in ngOnInit after translations are loaded
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['engagementStats'] && this.isTranslationLoaded) {
      setTimeout(() => {
        this.initDonutChart();
      }, 100);
    }
  }

  ngOnDestroy() {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    if (this.donutChart) {
      this.donutChart.destroy();
    }
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize(event: any) {
    // Debounce resize events to avoid excessive chart updates
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    this.resizeTimeout = setTimeout(() => {
      this.initDonutChart();
    }, 250);
  }

  private initDonutChart() {
    if (this.donutChartContainer && this.donutChartContainer.nativeElement) {
      const chartHeight = this.getChartHeight();
      this.donutChart = Highcharts.chart(this.donutChartContainer.nativeElement, {
        chart: {
          type: 'pie',
          height: chartHeight,
          backgroundColor: 'transparent',
          marginBottom: 0,
          marginTop: 0,
        },
        title: {
          text: this.translocoService.translate('ASSIGNMENT_ENGAGEMENT', {}, 'reportingV2'),
          align: 'left',
          style: {
            fontSize: '16px',
            fontWeight: '600',
            color: '#000'
          }
        },
        legend: {
          align: 'left',
          verticalAlign: 'top',
          y: 30,
          layout: 'vertical',
          symbolHeight: 0,
          symbolWidth: 0,
          symbolRadius: 0,
          itemStyle: {
            fontSize: '14px',
            cursor: 'default'
          },
          labelFormatter: function () {
            const point = this as any;
            return `<span style="color: ${point.color}">●</span> ${point.name} (${point.y})`;
          },
          events: {
            itemClick: function() {
              return false;
            }
          }
        },
        plotOptions: {
          pie: {
            innerSize: '45%', // This creates the donut effect
            dataLabels: {
              enabled: false
            },
            showInLegend: true, 
            allowPointSelect: false,
            states: {
              hover: {
                enabled: false
              },
              inactive: {
                enabled: false
              }
            },
          }
        },
        series: [{
          name: this.translocoService.translate('USERS', {}, 'reportingV2'),
          type: 'pie',
          data: this.getEngagementData()
        }],
        tooltip: {
          pointFormat: '{series.name}: <b>{point.y}</b><br/>',
          backgroundColor: 'white',
          borderColor: '#E4E7EC',
          borderRadius: 8,
          borderWidth: 1,
          shadow: true,
          style: {
            fontSize: '12px'
          }
        },
        credits: {
          enabled: false
        },
        accessibility: {
          enabled: false
        }
      });
    }
  }

  private getChartHeight(): number {
    const screenWidth = window.innerWidth;
    if (screenWidth <= 480) {
      return 200; 
    } else if (screenWidth <= 768) {
      return 240; 
    } else {
      return 280; 
    }
  }

  private getEngagementData(): any[] {
    if (!this.engagementStats) {
      return [
        {
          name: this.translocoService.translate('PROFICIENT_LABEL', {}, 'reportingV2'),
          y: 0,
          color: '#59DA94'
        },
        {
          name: this.translocoService.translate('IN_PROGRESS_LABEL', {}, 'reportingV2'),
          y: 0,
          color: '#52A4B0'
        },
        {
          name: this.translocoService.translate('ASSIGNED_LABEL', {}, 'reportingV2'),
          y: 0,
          color: '#EAECF0'
        }
      ];
    }

    const { completed, inProgress, notStarted } = this.engagementStats;
  
    return [
      {
        name: this.translocoService.translate('PROFICIENT_LABEL', {}, 'reportingV2'),
        y: completed,
        color: '#59DA94'
      },
      {
        name: this.translocoService.translate('IN_PROGRESS_LABEL', {}, 'reportingV2'),
        y: inProgress,
        color: '#52A4B0'
      },
      {
        name: this.translocoService.translate('ASSIGNED_LABEL', {}, 'reportingV2'),
        y: notStarted,
        color: '#EAECF0'
      }
    ];
  }

  protected formatTotalDuration(seconds: number): string {
    return formatHoursMinutesRounded(seconds);
  }
}